import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { type PendingAddition } from '@/lib/recurringBilling';

interface Expansion {
  key: string;
  clientName: string;
  productName: string;
  units: number;
  targetCutDate: string;
  amount: number;
}

export default function PendingExpansionsCard() {
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  useEffect(() => {
    fetchExpansions();
  }, []);

  async function fetchExpansions() {
    try {
      const { data: subs, error: subErr } = await supabase
        .from('erp_subscriptions')
        .select('id, client_id, product_id, pending_additions')
        .eq('status', 'active');

      if (subErr) throw subErr;

      const withQueue = (subs || []).filter(
        (s) => Array.isArray(s.pending_additions) && s.pending_additions.length > 0,
      );
      if (withQueue.length === 0) {
        setExpansions([]);
        setLoading(false);
        return;
      }

      const clientIds = [...new Set(withQueue.map((s) => s.client_id))];
      const productIds = [...new Set(withQueue.map((s) => s.product_id))];

      const [clientsRes, prodsRes] = await Promise.all([
        supabase.from('erp_clients').select('id, company_name').in('id', clientIds),
        supabase.from('erp_products').select('id, name, sku').in('id', productIds),
      ]);

      const clientMap = new Map((clientsRes.data || []).map((c) => [c.id, c.company_name]));
      const prodMap = new Map((prodsRes.data || []).map((p) => [p.id, p]));

      const rows: Expansion[] = [];
      withQueue.forEach((s) => {
        (s.pending_additions as PendingAddition[]).forEach((add, idx) => {
          rows.push({
            key: `${s.id}-${add.id || idx}`,
            clientName: clientMap.get(s.client_id) || 'Cliente',
            productName: prodMap.get(s.product_id)?.name || 'Servicio',
            units: Number(add.units) || 0,
            targetCutDate: add.target_cut_date || '',
            amount: Number(add.total_amount) || 0,
          });
        });
      });

      rows.sort((a, b) => (a.targetCutDate || '').localeCompare(b.targetCutDate || ''));
      setExpansions(rows);
    } catch (err) {
      console.error('Error fetching pending expansions:', err);
    } finally {
      setLoading(false);
    }
  }

  const totalUnits = expansions.reduce((s, e) => s + e.units, 0);

  const fmtCut = (d: string) => {
    if (!d) return '—';
    return new Date(d + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <div className="flex items-center justify-center h-24">
          <div className="w-5 h-5 border-2 border-accent-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
            <i className="ri-stack-line text-accent-600 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">Próximas Expansiones / Prorrateos en Cola</h3>
            <p className="text-[10px] text-foreground-500 mt-0.5">Unidades adicionales que se fusionarán en el próximo corte</p>
          </div>
        </div>
        {expansions.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-accent-100 text-accent-700 whitespace-nowrap">
            <i className="ri-add-line"></i>
            {totalUnits} uds en cola
          </span>
        )}
      </div>

      {expansions.length === 0 ? (
        <div className="py-10 text-center">
          <i className="ri-inbox-line text-foreground-300 text-2xl"></i>
          <p className="text-sm text-foreground-500 mt-2">Sin expansiones programadas</p>
          <p className="text-xs text-foreground-400 mt-1">Las unidades añadidas con prorrateo aparecerán aquí hasta su corte</p>
        </div>
      ) : (
        <div className="divide-y divide-background-100">
          {expansions.map((e) => (
            <div key={e.key} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-background-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-up-circle-line text-accent-600 text-base"></i>
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-medium text-foreground-800 block truncate">{e.clientName}</span>
                  <span className="text-[10px] text-foreground-500">{e.productName}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="text-right">
                  <span className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold">Unidades</span>
                  <span className="text-sm font-bold text-accent-700">+{e.units}</span>
                </div>
                <div className="text-right">
                  <span className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold">Corte objetivo</span>
                  <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">{fmtCut(e.targetCutDate)}</span>
                </div>
                <div className="text-right w-24">
                  <span className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold">Monto</span>
                  <span className="text-xs font-semibold text-foreground-700 whitespace-nowrap">{formatCurrency(e.amount)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}